'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  Brain,
  Sparkles,
  Shield,
  Zap,
  Code2,
  Users,
  CheckCircle2,
  Bot,
  Lightbulb,
  Target,
  MessageSquare,
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const levels = {
  beginner: {
    title: '入門コース',
    description: 'AIの基礎から生成AIの活用まで',
    duration: '全6回（各2時間）',
    modules: [
      {
        title: 'AI/生成AIの基礎理解',
        topics: ['AIとは何か', '生成AIの仕組み', '主要なAIツール紹介', '活用事例']
      },
      {
        title: '基本的なプロンプト作成',
        topics: ['効果的な質問の仕方', 'プロンプトの基本構造', '用途別テンプレート', '実践演習']
      },
      {
        title: '業務での活用方法',
        topics: ['文書作成支援', '情報収集・要約', 'アイデア出し', '翻訳・校正']
      }
    ]
  },
  intermediate: {
    title: '実践コース',
    description: '業務プロセスへのAI統合',
    duration: '全8回（各3時間）',
    modules: [
      {
        title: '高度なプロンプトエンジニアリング',
        topics: ['Chain of Thought', 'Few-shot learning', 'システムプロンプト設計', 'パラメータ調整']
      },
      {
        title: '業務自動化とワークフロー',
        topics: ['APIの活用', 'ノーコード連携', 'バッチ処理', 'エラーハンドリング']
      },
      {
        title: 'AI活用の効果測定',
        topics: ['KPI設定', 'ROI算出', 'A/Bテスト', '継続的改善']
      }
    ]
  },
  advanced: {
    title: 'アドバンスドコース',
    description: 'AI戦略立案とガバナンス',
    duration: '全10回（各3時間）',
    modules: [
      {
        title: 'AI戦略とロードマップ',
        topics: ['組織のAI成熟度評価', '導入戦略立案', '人材育成計画', '投資計画']
      },
      {
        title: 'AIガバナンスとリスク管理',
        topics: ['倫理的なAI利用', 'プライバシー保護', 'バイアス対策', 'セキュリティ']
      },
      {
        title: 'PoC実践プロジェクト',
        topics: ['課題定義', 'ソリューション設計', '実装・検証', '本格導入計画']
      }
    ]
  }
}

const useCases = [
  {
    industry: '自治体',
    title: '市民サービスの高度化',
    applications: [
      '問い合わせ対応の自動化',
      '文書作成・要約の効率化',
      '多言語対応の強化',
      'データ分析の高度化'
    ],
    result: '職員の業務時間40%削減'
  },
  {
    industry: '医療・福祉',
    title: '業務効率化と質の向上',
    applications: [
      'カルテ記載の支援',
      '診断補助情報の提供',
      'ケアプラン作成支援',
      '事務作業の自動化'
    ],
    result: '書類作成時間60%短縮'
  },
  {
    industry: '製造業',
    title: '生産性と品質の向上',
    applications: [
      '設計ドキュメント作成',
      '品質管理レポート',
      '技術情報の検索・整理',
      'トレーニング資料作成'
    ],
    result: 'ドキュメント作成効率3倍'
  },
  {
    industry: '小売・サービス',
    title: '顧客体験の革新',
    applications: [
      'パーソナライズ提案',
      'FAQ自動生成',
      'マーケティング文書作成',
      '在庫分析レポート'
    ],
    result: '顧客満足度20%向上'
  }
]

const features = [
  {
    icon: Bot,
    title: '実践的なハンズオン',
    description: '実際の業務シナリオに基づいた演習で、即座に活用できるスキルを習得'
  },
  {
    icon: Shield,
    title: '安全なAI活用',
    description: 'セキュリティとプライバシーを考慮した、責任あるAI利用方法を学習'
  },
  {
    icon: Lightbulb,
    title: '最新動向の共有',
    description: '日々進化するAI技術の最新トレンドと活用事例を定期的にアップデート'
  },
  {
    icon: Users,
    title: 'コミュニティ形成',
    description: '受講者同士の情報交換と継続的な学習を支援するネットワーク構築'
  }
]

export function AiEducationContent() {
  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 text-white py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('/images/ai-pattern.svg')] opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
        <div
          className="animate-fade-up container relative z-10"
        >
          <div className="max-w-4xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">一般〜アドバンスト対応</Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              AI教育
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              現場の生産性と再現可能な活用プロセス
            </p>
            <p className="text-lg mb-8 opacity-80">
              生成AIの業務設計、プロンプト/評価、ガバナンスとリスク、PoC演習。<br />
              実践的なカリキュラムで、AIを味方につけ、<br />
              組織の競争力を飛躍的に向上させます。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="secondary">
                <Sparkles className="mr-2 h-5 w-5" />
                AI活用度診断
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                カリキュラム詳細
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute -bottom-1 left-0 right-0">
          <svg viewBox="0 0 1440 100" className="w-full h-24 fill-background">
            <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" />
          </svg>
        </div>
      </section>

      {/* AI活用の可能性 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <Brain className="h-16 w-16 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">
              AIで変わる、仕事の未来
            </h2>
            <p className="text-lg text-muted-foreground">
              生成AIは単なるツールではなく、仕事のパートナー。<br />
              正しい知識と活用方法で、創造性と生産性を最大化します。
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: '生産性の飛躍的向上',
                description: 'ルーティン作業を自動化し、創造的な仕事に集中',
                metric: '業務時間 最大60%削減'
              },
              {
                icon: Target,
                title: '意思決定の質向上',
                description: 'データに基づく洞察で、より良い判断を迅速に',
                metric: '分析精度 85%向上'
              },
              {
                icon: Code2,
                title: 'イノベーションの加速',
                description: '新しいアイデアの創出と実験的な取り組み',
                metric: '新規提案 3倍増加'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full text-center">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mx-auto mb-4" />
                      <CardTitle>{benefit.title}</CardTitle>
                      <Badge className="mt-2">{benefit.metric}</Badge>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* レベル別コース */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">レベル別カリキュラム</h2>
            <p className="text-lg text-muted-foreground">
              初心者から上級者まで、段階的にスキルアップ
            </p>
          </FadeUp>

          <Tabs defaultValue="beginner" className="max-w-5xl mx-auto">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="beginner">入門</TabsTrigger>
              <TabsTrigger value="intermediate">実践</TabsTrigger>
              <TabsTrigger value="advanced">アドバンスド</TabsTrigger>
            </TabsList>

            {Object.entries(levels).map(([key, level]) => (
              <TabsContent key={key} value={key} className="mt-8">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-2xl">{level.title}</CardTitle>
                        <CardDescription>{level.description}</CardDescription>
                      </div>
                      <Badge variant="secondary">{level.duration}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {level.modules.map((module, idx) => (
                        <div key={idx} className="space-y-3">
                          <h4 className="font-semibold flex items-center gap-2">
                            <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm">
                              {idx + 1}
                            </span>
                            {module.title}
                          </h4>
                          <div className="ml-10 grid md:grid-cols-2 gap-2">
                            {module.topics.map((topic, topicIdx) => (
                              <div key={topicIdx} className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                                <span>{topic}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4 mt-8 pt-6 border-t">
                      <Button>
                        このコースに申し込む
                      </Button>
                      <Button variant="outline">
                        シラバスをダウンロード
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* 業界別活用事例 */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">業界別AI活用事例</h2>
            <p className="text-lg text-muted-foreground">
              様々な業界で実現されているAI活用の成果
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card>
                  <CardHeader>
                    <Badge className="w-fit mb-2">{useCase.industry}</Badge>
                    <CardTitle>{useCase.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold mb-2">主な活用シーン</p>
                        <ul className="space-y-1">
                          {useCase.applications.map((app, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <Bot className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                              <span>{app}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-4 border-t">
                        <p className="text-lg font-semibold text-primary">
                          {useCase.result}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* 特徴 */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">CodeFoxのAI教育の特徴</h2>
            <p className="text-lg text-muted-foreground">
              実践的で安全、そして継続的な学習環境
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <FadeUp delay={index * 0.1}
                  key={index}
                >
                  <Card className="h-full text-center">
                    <CardHeader>
                      <Icon className="h-12 w-12 text-primary mx-auto mb-4" />
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                </FadeUp>
              )
            })}
          </div>
        </div>
      </section>

      {/* 研修プラン */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">研修プラン</h2>
            <p className="text-lg text-muted-foreground">
              組織のニーズに合わせた柔軟なプラン設計
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                name: 'ベーシック',
                price: '月額 20万円〜',
                description: '少人数向け基礎研修',
                features: [
                  '入門コース（全6回）',
                  '受講者: 〜20名',
                  'オンライン形式',
                  '録画アーカイブ',
                  'Q&Aサポート'
                ]
              },
              {
                name: 'プロフェッショナル',
                price: '月額 50万円〜',
                description: '組織全体のAI活用推進',
                features: [
                  '入門〜実践コース',
                  '受講者: 〜100名',
                  'ハイブリッド形式',
                  'カスタムカリキュラム',
                  'ハンズオン演習',
                  '個別メンタリング'
                ],
                popular: true
              },
              {
                name: 'エンタープライズ',
                price: 'お問い合わせ',
                description: '全社的なAI変革支援',
                features: [
                  '全レベル対応',
                  '受講者: 無制限',
                  'オンサイト研修',
                  'PoC伴走支援',
                  'AI戦略コンサル',
                  '継続的なアップデート'
                ]
              }
            ].map((plan, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
              >
                <Card className={`h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
                  {plan.popular && (
                    <div className="bg-primary text-white text-center py-2 text-sm font-semibold">
                      おすすめ
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="text-3xl font-bold mt-4">{plan.price}</div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6" variant={plan.popular ? 'default' : 'outline'}>
                      {plan.name === 'エンタープライズ' ? 'お問い合わせ' : '詳細を見る'}
                    </Button>
                  </CardContent>
                </Card>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* AI活用度診断 */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto"
          >
            <Card className="border-primary/20">
              <CardHeader className="text-center">
                <Target className="h-16 w-16 text-primary mx-auto mb-4" />
                <CardTitle className="text-2xl">無料AI活用度診断</CardTitle>
                <CardDescription>
                  貴組織のAI活用レベルを診断し、最適な教育プランをご提案
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      '現在のAI活用状況',
                      '組織の課題と目標',
                      'ITリテラシーレベル',
                      '予算と期間'
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-primary" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="text-center pt-4">
                    <Button size="lg">
                      <Sparkles className="mr-2 h-5 w-5" />
                      無料診断を受ける
                    </Button>
                    <p className="text-sm text-muted-foreground mt-2">
                      所要時間: 約10分
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </FadeUp>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <Brain className="h-16 w-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              AIと共に創る、新しい働き方
            </h2>
            <p className="text-xl mb-8 opacity-90">
              今すぐ始めるAI活用。まずは無料相談から。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary">
                無料相談を申し込む
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                AI教育資料をダウンロード
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}